/**
 * ⑭ R14a — 탐색 계측 5종. 기존 user_events 규격(lib/analytics.ts `track(event_type, event_name, properties)`)을 그대로 탄다.
 * ⛔ 새 엔드포인트·새 큐를 만들지 않는다 — track() 이 /api/analytics/events 로 모아 보낸다.
 *
 *   nav_click          데스크톱 상단 탭·드롭다운 링크      event_name = 링크 id
 *   tab_click          모바일 하단 탭                      event_name = 탭 key
 *   more_click         /more 항목                          event_name = 항목 id
 *   region_chip_open   지역 칩 열기   (R14b 가 배선)       event_name = surface
 *   region_chip_apply  지역 칩 적용   (R14b 가 배선)       event_name = 지역 코드/이름
 *   search_open        검색 열기      (R14b 가 배선)       event_name = surface
 *   search_submit      검색 제출      (R14b 가 배선)       event_name = surface
 *
 * 마크업 쪽은 `data-nav-ev` / `data-nav-id` 속성만 단다(서버 HTML). 리스너는 NavV2Effects 섬 하나가 문서에 건다.
 */
import { track } from '@/lib/analytics';

export type NavEventType = 'nav_click' | 'tab_click' | 'more_click';
export const NAV_EV_ATTR = 'data-nav-ev';
export const NAV_ID_ATTR = 'data-nav-id';

/** 마크업에 붙일 속성 묶음(서버 컴포넌트에서 spread). */
export function navEvAttrs(ev: NavEventType, id: string): Record<string, string> {
  return { [NAV_EV_ATTR]: ev, [NAV_ID_ATTR]: id };
}

export function trackNavClick(id: string, props?: { href?: string; level?: 1 | 2; surface?: string }) {
  track('nav_click', id, props);
}
export function trackTabClick(key: string, props?: { href?: string }) {
  track('tab_click', key, props);
}
export function trackMoreClick(id: string, props?: { href?: string; group?: string }) {
  track('more_click', id, props);
}

/* ── R14b 용 도우미(배선은 R14b) ─────────────────────────────────────────── */
export function trackRegionChipOpen(surface: string, props?: { current?: string | null }) {
  track('region_chip_open', surface, props);
}
export function trackRegionChipApply(region: string, props?: { surface?: string; code?: string | null; href?: string }) {
  track('region_chip_apply', region, props);
}
export function trackSearchOpen(surface: string, props?: Record<string, unknown>) {
  track('search_open', surface, props);
}
export function trackSearchSubmit(surface: string, props?: { q_len?: number; region?: string | null; kind?: string }) {
  track('search_submit', surface, props);
}

/** 문서 위임 리스너 본체 — `[data-nav-ev]` 조상을 찾아 해당 이벤트를 보낸다. 테스트 가능하게 분리. */
export function handleNavClickTarget(el: Element | null): { ev: NavEventType; id: string; group?: string } | null {
  const hit = el?.closest?.(`[${NAV_EV_ATTR}]`) as HTMLElement | null | undefined;
  if (!hit) return null;
  const ev = hit.getAttribute(NAV_EV_ATTR) as NavEventType | null;
  const id = hit.getAttribute(NAV_ID_ATTR) || '';
  if (!ev || !id) return null;
  const href = hit.getAttribute('href') || undefined;
  const group = hit.getAttribute('data-nav-group') || undefined;
  if (ev === 'nav_click') trackNavClick(id, { href, level: hit.hasAttribute('data-nav-l2') ? 2 : 1 });
  else if (ev === 'tab_click') trackTabClick(id, { href });
  else if (ev === 'more_click') trackMoreClick(id, { href, group });
  else return null;
  return group ? { ev, id, group } : { ev, id };
}

/* ── R14b 호환(통합 시 합침) ────────────────────────────────────────────────
 * GlobalRegionChip·GlobalSearch 는 navTrack(name, props) 로 부른다. 규격을 위 도우미와 하나로 맞춘다:
 * event_type = 이벤트 이름(region_chip_open …), event_name = surface(variant). 계측 실패는 화면을 막지 않는다. */
export type NavEventName = 'region_chip_open' | 'region_chip_apply' | 'search_open' | 'search_submit';
export function navTrack(name: NavEventName, props?: Record<string, string | number | boolean | null>): void {
  try { track(name, String(props?.variant ?? props?.surface ?? ''), props ?? undefined); } catch { /* noop */ }
}
